"use client";

import { Plus, GripVertical, BarChart2, Save, X } from "lucide-react";
import { useT } from "@/hooks/useT";

interface WelcomeModalProps {
  onClose: () => void;
}

export default function WelcomeModal({ onClose }: WelcomeModalProps) {
  const t = useT();
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[260] flex items-center justify-center p-3 sm:p-4">
      <div className="bg-white dark:bg-[#18181D] rounded-[5px] border border-gray-200 dark:border-[#1A1A1A] w-full max-w-3xl max-h-[90dvh] overflow-auto shadow-xl">
        {/* Header */}
        <div className="p-5 sm:p-8 border-b border-gray-200 dark:border-[#1A1A1A]">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                {t("degen.welcome.title")}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t("degen.welcome.subtitle")}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-white/[0.06] rounded-[5px] transition-colors"
            >
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>
        </div>

        {/* Features */}
        <div className="p-5 sm:p-8 space-y-6">
          <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4">
            {t("degen.welcome.howItWorks")}
          </h3>

          <div className="space-y-4">
            <Feature
              icon={<Plus className="w-5 h-5" />}
              title={t("degen.welcome.feature.add.title")}
              description={t("degen.welcome.feature.add.desc")}
            />

            <Feature
              icon={<BarChart2 className="w-5 h-5" />}
              title={t("degen.welcome.feature.click.title")}
              description={t("degen.welcome.feature.click.desc")}
            />

            <Feature
              icon={<GripVertical className="w-5 h-5" />}
              title={t("degen.welcome.feature.drag.title")}
              description={t("degen.welcome.feature.drag.desc")}
            />

            <Feature
              icon={<BarChart2 className="w-5 h-5" />}
              title={t("degen.welcome.feature.toggle.title")}
              description={t("degen.welcome.feature.toggle.desc")}
            />

            <Feature
              icon={<Save className="w-5 h-5" />}
              title={t("degen.welcome.feature.saved.title")}
              description={t("degen.welcome.feature.saved.desc")}
            />
          </div>

          <div className="bg-gray-50 dark:bg-[#18181D] border border-gray-200 dark:border-[#1A1A1A] rounded-[5px] p-4 mt-6">
            <p className="text-sm text-gray-700 dark:text-gray-300">
              <strong>{t("degen.welcome.proTip")}</strong> {t("degen.welcome.proTipText")}
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 sm:p-6 border-t border-gray-200 dark:border-[#1A1A1A] flex items-center justify-between gap-3">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="w-4 h-4 rounded border-gray-300 text-gray-900 dark:text-white focus:ring-gray-400"
              onChange={(e) => {
                if (e.target.checked) {
                  localStorage.setItem("degenbox_welcome_dont_show", "true");
                }
              }}
            />
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {t("degen.welcome.dontShow")}
            </span>
          </label>

          <button
            onClick={onClose}
            className="px-6 py-2.5 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-[5px] hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors font-semibold text-sm"
          >
            {t("degen.welcome.start")}
          </button>
        </div>
      </div>
    </div>
  );
}

function Feature({
  icon,
  title,
  description,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
}) {
  return (
    <div className="flex gap-4">
      <div className="flex-shrink-0 w-10 h-10 rounded-[5px] bg-gray-100 dark:bg-[#0f0f12] flex items-center justify-center text-gray-600 dark:text-gray-300">
        {icon}
      </div>
      <div className="flex-1">
        <h4 className="font-semibold text-gray-900 dark:text-white mb-1 text-sm">
          {title}
        </h4>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {description}
        </p>
      </div>
    </div>
  );
}
